import { useEffect, useState } from "react";
import { LockKey, WarningCircle } from "@phosphor-icons/react";
import { Button, TextField } from "@radix-ui/themes";

import { AdminShell } from "./components/AdminShell";
import { Accounts } from "./features/Accounts";
import { CostLimits } from "./features/CostLimits";
import { Devices } from "./features/Devices";
import { Operations } from "./features/Operations";
import { Overview } from "./features/Overview";
import { Usage } from "./features/Usage";
import { Workspaces } from "./features/Workspaces";
import { configuration } from "./services/configuration";
import { signInAdmin, signOutAdmin, subscribeToAdminSession } from "./services/auth";

import type { FormEvent, ReactNode } from "react";
import type { AppState } from "./App.types";
import type { AdminRoute } from "./types";

const routePaths: Record<AdminRoute, string> = {
  overview: "/",
  accounts: "/accounts",
  workspaces: "/workspaces",
  devices: "/devices",
  costLimits: "/cost-limits",
  usage: "/usage",
  operations: "/operations",
};

const routeFromPath = (path: string): AdminRoute =>
  (Object.entries(routePaths).find(([, value]) => value === path)?.[0] as AdminRoute | undefined) ?? "overview";

const INITIAL_STATE: AppState = {
  route: routeFromPath(window.location.pathname),
  session: { status: "loading", user: null, message: null },
  error: null,
  isBusy: false,
};

export function App() {
  const [state, setState] = useState(INITIAL_STATE);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => subscribeToAdminSession((session) => setState((current) => ({ ...current, session, error: null }))), []);
  useEffect(() => {
    const onPopState = () => setState((current) => ({ ...current, route: routeFromPath(window.location.pathname) }));
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const navigate = (route: AdminRoute) => {
    const path = routePaths[route];
    if (window.location.pathname !== path) window.history.pushState(null, "", path);
    setState((current) => ({ ...current, route }));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setState((current) => ({ ...current, isBusy: true, error: null }));
    try { await signInAdmin(email, password); }
    catch (reason) { setState((current) => ({ ...current, error: reason instanceof Error ? reason.message : "Oturum açılamadı." })); }
    finally { setState((current) => ({ ...current, isBusy: false })); }
  };

  if (state.session.status === "loading") return <main className="auth-screen"><div className="loader" /><p>Yönetici oturumu kontrol ediliyor…</p></main>;
  if (state.session.status === "signed-out") return (
    <main className="auth-screen"><form className="login-card" onSubmit={(event) => void submit(event)}><span className="login-icon"><LockKey size={24} /></span><div><span className="kicker">TAYAN KONTROL DÜZLEMİ</span><h1>Yönetim paneli</h1><p>Devam etmek için platform yöneticisi hesabınızla oturum açın.</p></div><label><span>E-posta</span><TextField.Root autoComplete="username" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label><label><span>Parola</span><TextField.Root autoComplete="current-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} required /></label><Button disabled={state.isBusy} type="submit">{state.isBusy ? "Doğrulanıyor…" : "Oturum aç"}</Button>{state.error ? <div className="login-error"><WarningCircle size={18} />{state.error}</div> : null}</form></main>
  );
  if (state.session.status === "forbidden") return <main className="auth-screen"><section className="login-card"><WarningCircle className="forbidden-icon" size={36} /><h1>Erişim reddedildi</h1><p>{state.session.message}</p><Button onClick={() => void signOutAdmin()} type="button">Çıkış yap</Button></section></main>;

  const pages: Record<AdminRoute, ReactNode> = {
    overview: <Overview />, accounts: <Accounts />, workspaces: <Workspaces />,
    devices: <Devices />, costLimits: <CostLimits />, usage: <Usage />, operations: <Operations />,
  };
  return <AdminShell activeRoute={state.route} mode={configuration.mode} onNavigate={navigate} onSignOut={() => void signOutAdmin()} user={state.session.user}>{pages[state.route]}</AdminShell>;
}
